import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';
import AppTheme from './shared-theme/AppTheme';
import { AuthProvider } from './context/AuthContext';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import HomePage from './pages/HomePage';
import SignUpPage from './pages/SignUp';
import SignInPage from './pages/SignIn';
import OnboardingPage from './pages/OnboardingPage';
import MyAccountPage from './pages/MyAccount';
import NotFoundPage from './pages/NotFoundPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import NewPostPage from './pages/NewPostPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import C4SolverPage from './pages/C4SolverPage';
import UserProfilePage from './pages/UserProfilePage';

function PageTransition({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  return (
    <Box sx={isHome ? undefined : { pt: '72px' }}>
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/SignUp" element={<PageTransition><SignUpPage /></PageTransition>} />
        <Route path="/SignIn" element={<PageTransition><SignInPage /></PageTransition>} />

        {/* Blog — publicly readable; create/edit are gated inside the components */}
        <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
        <Route path="/projects" element={<PageTransition><ProjectsPage /></PageTransition>} />
        <Route path="/projects/c4solver" element={<PageTransition><C4SolverPage /></PageTransition>} />
        {/* /blog/new must be declared before /blog/:postId so "new" isn't treated as a postId */}
        <Route
          path="/blog/new"
          element={
            <PageTransition>
              <ProtectedRoute requireProfile>
                <NewPostPage />
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route path="/blog/:postId" element={<PageTransition><BlogPostPage /></PageTransition>} />
        <Route path="/profile/:userId" element={<PageTransition><UserProfilePage /></PageTransition>} />
        <Route
          path="/blog/:postId/edit"
          element={
            <PageTransition>
              <ProtectedRoute requireProfile>
                <NewPostPage />
              </ProtectedRoute>
            </PageTransition>
          }
        />

        {/* Authenticated user routes */}
        <Route
          path="/onboarding"
          element={
            <PageTransition>
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            </PageTransition>
          }
        />
        <Route
          path="/account"
          element={
            <PageTransition>
              <ProtectedRoute requireProfile>
                <MyAccountPage />
              </ProtectedRoute>
            </PageTransition>
          }
        />

        {/* Admin-only routes */}
        <Route
          path="/admin"
          element={
            <PageTransition>
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            </PageTransition>
          }
        />
        <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
      </Routes>
    </AnimatePresence>
    </Box>
  );
}

export default function MyApp() {
  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <AuthProvider>
        <Router>
          <NavBar />
          <AnimatedRoutes />
          <Footer />
        </Router>
      </AuthProvider>
    </AppTheme>
  );
}
